import React, { useState, useEffect } from 'react';
import {
  Store, TrendingUp, TrendingDown, DollarSign, RefreshCw, Filter,
  ChevronDown, ChevronUp, AlertTriangle, Info, Percent, Package
} from 'lucide-react';
import api, { API_URL } from '../../services/api';

const RapportBoutiques = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const today = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })();
  const [dateDebut, setDateDebut] = useState(today.slice(0, 8) + '01');
  const [dateFin, setDateFin] = useState(today);
  const [showFilters, setShowFilters] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [repartirCharges, setRepartirCharges] = useState(false);

  const fmt = (m) => parseFloat(m||0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' DH';
  const fmtPct = (v) => (v !== null && v !== undefined) ? `${v.toFixed ? v.toFixed(1) : v}%` : '-';

  const loadData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateDebut) params.append('date_debut', dateDebut);
      if (dateFin) params.append('date_fin', dateFin);
      if (repartirCharges) params.append('repartir_charges', '1');
      const r = await api.get(`${API_URL}/rapports/boutiques?${params.toString()}`);
      setData(r.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadData(); /* eslint-disable-next-line */ }, [repartirCharges]);

  if (loading) return <div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div></div>;

  const boutiques = data?.boutiques || [];
  const t = data?.totaux || {};

  const pctSansCout = t.nb_ventes_total > 0 ? (t.nb_bijoux_sans_cout / t.nb_ventes_total * 100) : 0;

  return (
    <div className="min-h-screen bg-gray-100 pb-8">
      <div className="bg-gradient-to-r from-indigo-700 to-blue-700 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold">Rapport Rentabilite Boutiques</h1>
              <p className="text-indigo-200 text-sm">CA - Cout bijoux = Marge brute - Depenses = Resultat net</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowFilters(!showFilters)} className="flex items-center space-x-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg">
                <Filter className="h-4 w-4" /><span>Periode</span>
              </button>
              <button onClick={loadData} className="bg-white/20 hover:bg-white/30 p-2.5 rounded-lg"><RefreshCw className="h-4 w-4" /></button>
            </div>
          </div>
          {showFilters && (
            <div className="mt-4 bg-white/10 rounded-xl p-4 flex flex-col sm:flex-row items-end gap-3 flex-wrap">
              <div><label className="block text-xs text-indigo-200 mb-1">Du</label><input type="date" value={dateDebut} onChange={e => setDateDebut(e.target.value)} className="px-3 py-2 rounded-lg bg-white/20 text-white border border-white/30" /></div>
              <div><label className="block text-xs text-indigo-200 mb-1">Au</label><input type="date" value={dateFin} onChange={e => setDateFin(e.target.value)} className="px-3 py-2 rounded-lg bg-white/20 text-white border border-white/30" /></div>
              <button onClick={loadData} className="px-6 py-2 bg-white text-indigo-700 font-medium rounded-lg">Appliquer</button>
              <label className="flex items-center gap-2 cursor-pointer ml-auto">
                <input type="checkbox" checked={repartirCharges} onChange={e => setRepartirCharges(e.target.checked)} className="rounded" />
                <span className="text-sm">Repartir charges centrales au prorata CA</span>
              </label>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6 space-y-6">
        {/* Alerte si beaucoup de bijoux sans cout */}
        {t.nb_bijoux_sans_cout > 0 && pctSansCout > 10 && (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-6 w-6 text-yellow-600 flex-shrink-0" />
              <div>
                <h3 className="font-bold text-yellow-900">Attention : cout manquant pour {t.nb_bijoux_sans_cout}/{t.nb_ventes_total} ventes ({pctSansCout.toFixed(1)}%)</h3>
                <p className="text-sm text-yellow-800 mt-1">
                  Les anciennes ventes pointent vers des bijoux supprimes ou sans cout rempli en base. Les chiffres de <strong>marge et resultat net</strong> ne reflent donc que {t.nb_ventes_total - t.nb_bijoux_sans_cout} ventes sur {t.nb_ventes_total}.
                  Le <strong>CA total</strong> reste correct ({fmt(t.ca)}).
                </p>
                <p className="text-xs text-yellow-700 mt-2">
                  Pour les nouvelles productions, assurez-vous que le cout reel (metal + pierres + main d'oeuvre + perte or) soit bien renseigne a la fin de la fabrication.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* LIGNE 1 : Compte de resultat global */}
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <div className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white px-5 py-3">
            <h2 className="font-bold text-lg flex items-center gap-2"><DollarSign className="h-5 w-5" /> COMPTE DE RESULTAT GLOBAL</h2>
          </div>
          <div className="p-5 space-y-3">
            <div className="flex items-center justify-between py-2 border-b">
              <div>
                <p className="font-medium text-gray-700">Chiffre d'affaires (CA)</p>
                <p className="text-xs text-gray-500">{t.nb_ventes_total} ventes sur la periode</p>
              </div>
              <p className="text-2xl font-black text-gray-900">{fmt(t.ca)}</p>
            </div>

            <div className="flex items-center justify-between py-2 border-b">
              <div>
                <p className="font-medium text-gray-700">- Cout des bijoux vendus</p>
                <p className="text-xs text-gray-500">Matieres + main d'oeuvre + perte or + montant deposants</p>
                {t.nb_bijoux_sans_cout > 0 && (
                  <p className="text-xs text-yellow-600 mt-1">⚠ {t.nb_bijoux_sans_cout} bijoux sans cout renseigne</p>
                )}
              </div>
              <p className="text-2xl font-black text-red-700">-{fmt(t.cout_bijoux)}</p>
            </div>

            <div className="flex items-center justify-between py-3 bg-green-50 rounded-lg px-3">
              <div>
                <p className="font-bold text-green-800">= MARGE BRUTE</p>
                <p className="text-xs text-green-600">Ce que rapportent les bijoux avant depenses</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-black text-green-700">{fmt(t.marge_brute)}</p>
                <p className="text-sm text-green-600 font-bold">{fmtPct(t.marge_brute_pct)} du CA</p>
              </div>
            </div>

            <div className="flex items-center justify-between py-2 border-b">
              <div>
                <p className="font-medium text-gray-700">- Depenses boutiques</p>
                <p className="text-xs text-gray-500">Loyer, salaires, entretien, amenagement par boutique</p>
              </div>
              <p className="text-xl font-bold text-red-700">-{fmt(t.depenses_boutiques)}</p>
            </div>

            <div className="flex items-center justify-between py-2 border-b">
              <div>
                <p className="font-medium text-gray-700">- Charges centrales</p>
                <p className="text-xs text-gray-500">Matieres premieres, pierres achetees, divers non affectes</p>
                {t.nb_charges_centrales > 0 && <p className="text-xs text-gray-400">{t.nb_charges_centrales} depenses</p>}
              </div>
              <p className="text-xl font-bold text-red-700">-{fmt(t.charges_centrales)}</p>
            </div>

            <div className={`flex items-center justify-between py-3 rounded-lg px-3 ${t.resultat_net >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
              <div>
                <p className={`font-bold ${t.resultat_net >= 0 ? 'text-green-800' : 'text-red-800'}`}>= RESULTAT NET</p>
                <p className={`text-xs ${t.resultat_net >= 0 ? 'text-green-600' : 'text-red-600'}`}>Benefice ou perte de l'entreprise</p>
              </div>
              <div className="text-right">
                <p className={`text-3xl font-black ${t.resultat_net >= 0 ? 'text-green-700' : 'text-red-700'}`}>{fmt(t.resultat_net)}</p>
                <p className={`text-sm font-bold ${t.resultat_net >= 0 ? 'text-green-600' : 'text-red-600'}`}>{fmtPct(t.resultat_pct)} du CA</p>
              </div>
            </div>
          </div>
        </div>

        {/* LIGNE 2 : Cartes boutiques */}
        <div>
          <h2 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
            <Store className="h-5 w-5 text-indigo-600" /><span>DETAIL PAR BOUTIQUE</span>
            <span className="text-sm text-gray-500 font-normal ml-auto">
              {repartirCharges ? '(charges centrales reparties au prorata du CA)' : '(charges centrales separees)'}
            </span>
          </h2>

          <div className="space-y-3">
            {boutiques.filter(b => b.nb_ventes > 0 || b.depenses_boutique > 0).map(b => {
              const isExp = expanded === b.boutique_id;
              const netColor = b.resultat_net >= 0 ? 'text-green-700' : 'text-red-700';
              const bgColor = b.resultat_net >= 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200';

              return (
                <div key={b.boutique_id} className={`bg-white rounded-xl shadow-sm border overflow-hidden ${b.resultat_net === 0 ? '' : ''}`}>
                  <div className="p-4 cursor-pointer hover:bg-gray-50" onClick={() => setExpanded(isExp ? null : b.boutique_id)}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <Store className="h-6 w-6 text-indigo-600" />
                        <h3 className="text-lg font-bold text-gray-900">{b.boutique_nom}</h3>
                        {b.nb_bijoux_sans_cout > 0 && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" />{b.nb_bijoux_sans_cout} sans cout
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className={`text-xl font-black ${netColor}`}>{fmt(b.resultat_net)}</p>
                          <p className="text-xs text-gray-500">Net ({fmtPct(b.resultat_pct)})</p>
                        </div>
                        {isExp ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
                      </div>
                    </div>

                    {/* Mini compte de resultat */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                      <div>
                        <p className="text-xs text-gray-500">CA ({b.nb_ventes} ventes)</p>
                        <p className="font-bold text-gray-900">{fmt(b.ca)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">- Cout bijoux</p>
                        <p className="font-bold text-red-600">{fmt(b.cout_bijoux)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">= Marge brute</p>
                        <p className="font-bold text-green-700">{fmt(b.marge_brute)} <span className="text-xs font-normal">({fmtPct(b.marge_brute_pct)})</span></p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">- Depenses</p>
                        <p className="font-bold text-red-600">{fmt(b.depenses_totales)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Detail depenses */}
                  {isExp && (
                    <div className="border-t bg-gray-50 px-5 py-4 space-y-3">
                      {b.nb_bijoux_sans_cout > 0 && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm">
                          <p className="text-yellow-800">
                            <AlertTriangle className="h-4 w-4 inline mr-1" />
                            <strong>{b.nb_bijoux_sans_cout} bijoux</strong> sans cout renseigne representent <strong>{fmt(b.ca_sans_cout)}</strong> de CA dans cette boutique. La marge calculee ne concerne que les ventes avec cout connu.
                          </p>
                        </div>
                      )}

                      {b.categories && b.categories.length > 0 && (
                        <div>
                          <h4 className="text-xs font-bold text-gray-500 uppercase mb-2">Depenses par categorie</h4>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                            {b.categories.map((c, i) => (
                              <div key={i} className="flex items-center justify-between bg-white rounded-lg p-2 border">
                                <div className="flex items-center gap-2">
                                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: c.couleur || '#6B7280' }} />
                                  <span className="text-sm text-gray-700">{c.categorie_nom || 'Non categorise'}</span>
                                </div>
                                <span className="font-bold text-red-600 text-sm">{fmt(c.total)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {repartirCharges && b.charges_centrales_affectees > 0 && (
                        <div className="text-sm text-gray-600">
                          <span className="font-medium">Charges centrales affectees :</span> {fmt(b.charges_centrales_affectees)}
                          <span className="text-xs text-gray-400 ml-2">(au prorata du CA)</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Info si charges centrales */}
        {!repartirCharges && t.charges_centrales > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-600 flex-shrink-0" />
            <div>
              <p className="text-sm text-blue-800">
                <strong>{fmt(t.charges_centrales)}</strong> de charges centrales ({t.nb_charges_centrales} depenses) ne sont pas affectees aux boutiques.
                Activez "Repartir charges centrales au prorata CA" dans les filtres pour les repartir.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RapportBoutiques;
